import { useEffect, useRef, type ReactNode } from 'react'
import { AlertTriangle, Check } from '@/components/ui/icons'
import { motion } from '@/utils/motion'
import { cn } from '@/utils/cn'

export type NoticeTone = 'notice' | 'alert'

interface NoticeProps {
  tone?: NoticeTone
  /** Optional lead line, when the outcome needs naming before it is explained. */
  title?: string
  children: ReactNode
  className?: string
}

const RAIL: Record<NoticeTone, string> = {
  notice: 'border-l-notice-rail-notice text-notice-rail-notice',
  alert: 'border-l-notice-rail-alert text-notice-rail-alert',
}

/**
 * A message that stays on the page: the outcome of a form, or the state a page
 * is now in. Its transient sibling is `FlashBanner`, and the two share one
 * vocabulary — a coloured rail down the leading edge, a glyph, and the message
 * in the reading serif — so an outcome looks the same wherever it surfaces.
 *
 * The tone is carried by the rail *and* the glyph, never by a fill: a panel
 * repainted in a signal colour shouts over everything around it, and colour on
 * its own tells a colour-blind reader nothing.
 */
export function Notice({ tone = 'notice', title, children, className }: NoticeProps) {
  const ref = useRef<HTMLDivElement>(null)
  const Glyph = tone === 'alert' ? AlertTriangle : Check

  useEffect(() => {
    if (ref.current) void motion.rise(ref.current, { dy: 8 })
  }, [])

  return (
    <div
      ref={ref}
      role={tone === 'alert' ? 'alert' : 'status'}
      aria-live={tone === 'alert' ? 'assertive' : 'polite'}
      className={cn(
        'w-full flex items-start gap-3 rounded-md border border-border-divider border-l-[3px]',
        'bg-surface-bg px-4 py-4 sm:px-5',
        RAIL[tone],
        className,
      )}
    >
      <Glyph size={16} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        {title ? (
          <p className="font-serif text-base text-heading">{title}</p>
        ) : null}
        <p className={cn('font-serif text-sm leading-relaxed text-body', title && 'mt-1 text-muted')}>
          {children}
        </p>
      </div>
    </div>
  )
}
